import { ApiError } from '../../../shared/api-client/index.js';
import { createEntityMutations } from '../../../shared/api/createEntityMutations.js';

import {
  createContact,
  deleteContact,
  updateContact,
  type ContactItem,
  type CreateContactInput,
  type UpdateContactInput,
} from '../api/contacts.client.js';

export interface DuplicateContactSuggestion {
  id: string;
  fullName: string;
  email?: string | null;
}

export class DuplicateContactError extends Error {
  public readonly suggestion: DuplicateContactSuggestion;

  constructor(suggestion: DuplicateContactSuggestion) {
    super('duplicate contact');
    this.name = 'DuplicateContactError';
    this.suggestion = suggestion;
  }
}

/**
 * 409 от сервера — это не отказ, а подсказка «такой контакт уже есть»: форма
 * показывает ссылку на найденный контакт. Поэтому создание контакта — единственная
 * мутация со своим `mutationFn`, всё остальное общее.
 */
async function createContactMappingDuplicate(input: CreateContactInput): Promise<ContactItem> {
  try {
    return await createContact(input);
  } catch (error) {
    const status =
      error instanceof ApiError
        ? error.status
        : typeof error === 'object' && error !== null && 'status' in error
          ? error.status
          : undefined;
    const details =
      error instanceof ApiError
        ? error.details
        : typeof error === 'object' && error !== null && 'details' in error
          ? error.details
          : undefined;

    if (status === 409) {
      const suggestion = (details as { suggestion?: DuplicateContactSuggestion } | undefined)
        ?.suggestion;
      if (suggestion) {
        throw new DuplicateContactError(suggestion);
      }
    }

    throw error instanceof Error ? error : new Error('failed to create contact');
  }
}

const contactMutations = createEntityMutations<ContactItem, CreateContactInput, UpdateContactInput>(
  {
    key: 'contacts',
    // Контакт виден в панелях сделки и компании — их выборки тоже устаревают.
    alsoInvalidate: ['deals', 'companies'],
    create: createContactMappingDuplicate,
    update: updateContact,
    remove: deleteContact,
  },
);

export const useCreateContactMutation = contactMutations.useCreate;
export const useUpdateContactMutation = contactMutations.useUpdate;
export const useDeleteContactMutation = contactMutations.useDelete;
